import '../themes/navigation.scss';

import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router';
import { ConfirmPopup, SelectProfilePopup } from '~/commonComponents';
import { SelectProfessionTypePopup } from '~/components/ShiftRequestRedesign/SelectProfessionType/SelectProfessionTypePopup';
import { useSelectProfessionType } from '~/components/ShiftRequestRedesign/SelectProfessionType/useSelectProfessionType';
import { useUserSelector } from '~/hooks/useUserSelector';
import WarningIcon from '~/images/icons/Warning';
import { routes } from '~/routes';
import { useIsReferralProgramVisible } from '~/routes/hooks';
import { useIsWorkerClassificationEnabled, useRequestControls } from '~/selectors';
import {
  fireMenuBarDashboardClicked,
  fireMenuBarScheduleClicked,
  fireMenuBarProfessionalHubClicked,
  fireMenuBarReferralClicked,
} from '~/posthogs/events';
import { POSTHOG_EVENTS, trackPosthogEvent } from '~/services/posthog';
import { searchChildOffice, selectOffice } from '../actions';
import browserHistory from '../browserHistory';
import { ShiftRequestRedesign } from '../constants/ff.posthog';
import {
  getShouldEnableReferralProgramV2,
  getShouldEnforcePermanentJobV2,
  getShouldUseSidebarRequestProfessionalMenu,
} from '../growthbook';
import { useFeatureFlagVariant } from '../hooks/useFeatureFlagVariant';
import { BriefcaseIcon } from '../images/icons/BriefcaseIcon';
import { CalendarIcon } from '../images/icons/CalendarIcon';
import { DashboardIcon } from '../images/icons/DashboardIcon';
import { GiftIcon } from '../images/icons/GiftIcon';
import { ProfessionalRequestIcon } from '../images/icons/ProfessionalRequestIcon';
import { UserGroup } from '../images/icons/UserGroup';
import { Colors } from '../themes/colors';
import { useRecommendedProsFeature } from './ProfessionalsHub/hooks/useRecommendedProsFeature';

const RenderLink = ({ route, isActive = false, Icon, title, isNew, listener }) => {
  const [isHovering, setIsHovering] = useState(false);

  const handleMouseOver = () => {
    setIsHovering(true);
  };

  const handleMouseOut = () => {
    setIsHovering(false);
  };

  const foregroundColor = isActive ? Colors.white : Colors.neutral_600;
  const backgroundColor = isActive
    ? Colors.primary_500
    : isHovering
      ? Colors.neutral_100
      : undefined;

  return (
    <div
      style={{
        padding: '9px 16px 9px 32px',
        marginRight: 18,
        marginLeft: 18,
        ...(backgroundColor && {
          backgroundColor,
          borderRadius: '6px',
        }),
      }}
      onClick={() => listener?.()}
    >
      <Link
        to={route}
        onMouseOver={handleMouseOver}
        onMouseOut={handleMouseOut}
        style={{
          color: foregroundColor,
          fontFamily: 'Nunito',
          fontSize: 20,
          fontWeight: 'bold',
          textDecoration: 'none',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 20,
          }}
        >
          <Icon color={foregroundColor} />

          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 4,
            }}
          >
            {title}
            {isNew && (
              <div
                style={{
                  fontFamily: 'Nunito',
                  fontSize: 12,
                  fontWeight: 700,
                  backgroundColor: Colors.secondary_500,
                  color: Colors.white,
                  width: 48,
                  height: 20,
                  borderRadius: 3,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                NEW
              </div>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
};

export default function SideBar() {
  const dispatch = useDispatch();
  const { location } = browserHistory;
  const childOffices = useSelector((state) => state.user.childOffices);
  const allPPJobs = useSelector((state) => state.pp.allPPJobs);
  const user = useUserSelector();
  const shouldUseShiftRequestRedesign = useFeatureFlagVariant(ShiftRequestRedesign, 'test');
  const { isRecommendedProsFeatureNew } = useRecommendedProsFeature();

  const allowedRequest = user?.allowedRequest || {};
  const [shiftRequestsUnavailableModalOpen, setShiftRequestsUnavailableModalOpen] = useState(false);
  const isWorkerClassificationEnabled = useIsWorkerClassificationEnabled();
  const showWorkerClassificationWarning =
    isWorkerClassificationEnabled && !user?.workerClassification?.classificationType;

  const [showProfessionPopup, setShowProfessionPopup] = useState(false);
  const officeState = user?.address?.[0]?.state ?? '';
  const { availableProfessions } = useSelectProfessionType({ officeState });

  const allowedRequestProfessionalMenuStates = getShouldUseSidebarRequestProfessionalMenu();
  const isAllowedRequestProfessionalMenuEnabled =
    allowedRequestProfessionalMenuStates.length > 0 &&
    allowedRequestProfessionalMenuStates.includes(user.address?.[0]?.state);

  const isProfessionalHubNew = useMemo(() => {
    return (
      isRecommendedProsFeatureNew || !localStorage.getItem(`isNewToYouTabNew-${user?.id}`)
    );
  }, [isRecommendedProsFeatureNew, user?.id]);

  const { isPermProductAllowed } = useRequestControls();
  const hasPostings = useSelector((state) => state.permanentJobs.hasPostings);
  const hasPublishedPostings = useSelector((state) => state.permanentJobs.hasPublishedPostings);
  const shouldShowReferralProgram = useIsReferralProgramVisible();

  const renderProfileSelection = () => {
    if (childOffices && childOffices.length !== 0) {
      return (
        <div style={{ marginTop: 30, display: 'flex', flexDirection: 'column' }}>
          <SelectProfilePopup
            onOfficeClicked={(officeId) => dispatch(selectOffice({ officeId }))}
            onSearch={(searchText) => dispatch(searchChildOffice({ searchText }))}
          />
        </div>
      );
    }

    return <div style={{ height: 40 }} />;
  };

  const renderPPLink = () => {
    if (allPPJobs && allPPJobs.length === 1) {
      return (
        <RenderLink
          route={`/dashboard/permanentJobs/${allPPJobs[0].id}`}
          isActive={location.pathname.includes('/dashboard/permanentJobs')}
          Icon={BriefcaseIcon}
          title="Permanent Job"
        />
      );
    }
    if (allPPJobs && allPPJobs.length === 0) {
      return null;
    }
    return (
      <RenderLink
        route="/dashboard/permanentJobs"
        isActive={location.pathname.includes('/dashboard/permanentJobs')}
        Icon={BriefcaseIcon}
        title="Permanent Jobs"
      />
    );
  };

  return (
    <div className="side_bar_contain">
      <div className="navigation_contain">
        {renderProfileSelection()}
        <div
          style={{
            marginTop: '10px',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            gap: 18,
          }}
        >
          <RenderLink
            route="/"
            isActive={location.pathname === '/'}
            Icon={DashboardIcon}
            title="Dashboard"
            listener={fireMenuBarDashboardClicked}
          />
          {isAllowedRequestProfessionalMenuEnabled && allowedRequest?.tempOrMdbShifts && (
            <RenderLink
              route={
                shouldUseShiftRequestRedesign
                  ? location.pathname
                  : routes.multiDayShiftRequest
              }
              isActive={
                location.pathname.startsWith('/dashboard/shift-request/') ||
                location.pathname.startsWith('/dashboard/multi-day/')
              }
              Icon={ProfessionalRequestIcon}
              title="Post Shifts"
              listener={() => {
                if (showWorkerClassificationWarning) {
                  setShiftRequestsUnavailableModalOpen(true);
                  return;
                }
                trackPosthogEvent(POSTHOG_EVENTS.OFFICE_MENU_BAR_SHIFT_REQUEST_CLICKED);
                if (shouldUseShiftRequestRedesign) {
                  setShowProfessionPopup(true);
                }
              }}
            />
          )}
          {renderPPLink()}
          <RenderLink
            route="/dashboard/calendar"
            isActive={location.pathname === '/dashboard/calendar'}
            Icon={CalendarIcon}
            title="Schedule"
            listener={fireMenuBarScheduleClicked}
          />
          <RenderLink
            route="/professionals-hub/favorites-list"
            isActive={location.pathname.includes('/professionals-hub')}
            Icon={UserGroup}
            title="Professionals Hub"
            isNew={isProfessionalHubNew}
            listener={fireMenuBarProfessionalHubClicked}
          />
          {getShouldEnforcePermanentJobV2() && isPermProductAllowed && (
            <RenderLink
              Icon={BriefcaseIcon}
              isActive={location.pathname.startsWith('/permanent-jobs')}
              route={
                hasPostings === false
                  ? '/permanent-jobs/value-proposition'
                  : '/permanent-jobs/postings'
              }
              title="Permanent Jobs"
              isNew={hasPublishedPostings === false}
            />
          )}
          {shouldShowReferralProgram && (
            <RenderLink
              route="/dashboard/referral"
              isActive={location.pathname === '/dashboard/referral'}
              Icon={GiftIcon}
              title="Referral Program"
              isNew={
                getShouldEnableReferralProgramV2() &&
                !localStorage.getItem(`isReferralProgramNew-${user.id}`)
              }
              listener={() => {
                fireMenuBarReferralClicked();
                if (getShouldEnableReferralProgramV2()) {
                  localStorage.setItem(`isReferralProgramNew-${user.id}`, 'false');
                }
              }}
            />
          )}
        </div>
      </div>

      {shiftRequestsUnavailableModalOpen && showWorkerClassificationWarning && (
        <ConfirmPopup
          Icon={() => (
            <div style={{ marginBottom: 20 }}>
              <WarningIcon size={80} color={Colors.warning_500} />
            </div>
          )}
          title="Shift Requests Unavailable"
          description="Shift Requests are unavailable until a worker type has been chosen for this office. Please visit the Worker Type Settings page to select a preference."
          descriptionStyle={{ marginBottom: 40 }}
          leftButtonAction={() => setShiftRequestsUnavailableModalOpen(false)}
          leftButtonText="Close"
          leftButtonStyle={{
            width: 170,
          }}
          rightButtonAction={() => {
            setShiftRequestsUnavailableModalOpen(false);
            browserHistory.push(routes.workerClassificationType);
          }}
          rightButtonText="Set Preference"
          rightButtonStyle={{
            width: 170,
          }}
        />
      )}

      {showProfessionPopup && (
        <SelectProfessionTypePopup
          professions={availableProfessions}
          onContinue={(professionCode) => {
            setShowProfessionPopup(false);
            browserHistory.push(routes.selectDates, {
              professionType: professionCode,
            });
          }}
          onClose={() => setShowProfessionPopup(false)}
        />
      )}
    </div>
  );
}
